import React, { Suspense, lazy, useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import Nav from "./Nav";
import Hero from "./Hero";
import Bento from "./Bento";
import Gallery from "./Gallery";
import Pricing from "./Pricing";
import Faq from "./Faq";
import Contact from "./Contact";
import Footer from "./Footer";
import OrderModal from "./OrderModal";
import { META_DESCRIPTION_HOME, PAGE_TITLE_HOME } from "@/lib/siteSeo";
import { forceUnlockPageScroll, getScrollLockState } from "@/lib/scrollLock";
import { I18nProvider } from "@/i18n/I18nContext";
import { ThemeProvider } from "@/theme/ThemeContext";

const TrainersModal = lazy(() => import("./TrainersModal"));
const AerobicInfoModal = lazy(() => import("./AerobicInfoModal"));

const MaxFitnessInner = () => {
  const location = useLocation();
  const [orderState, setOrderState] = useState({ open: false, planId: null });
  const [trainersOpen, setTrainersOpen] = useState(false);
  const [aerobicInfoOpen, setAerobicInfoOpen] = useState(false);

  const openOrder = useCallback((planId) => {
    setOrderState({ open: true, planId });
  }, []);
  const scrollToPricing = useCallback(() => {
    if (typeof window === "undefined" || typeof document === "undefined") return;

    const pricingSection = document.getElementById("pricing");
    if (!pricingSection) return;

    const nav = document.querySelector("[data-testid='site-nav']");
    const scroll = () => {
      const navHeight = nav?.getBoundingClientRect().height || 0;
      const sectionTop = pricingSection.getBoundingClientRect().top + window.scrollY;
      const targetTop = Math.max(0, sectionTop - navHeight - 16);

      window.scrollTo({ top: targetTop, behavior: "smooth" });
    };

    scroll();
    window.requestAnimationFrame(() => {
      scroll();
      window.requestAnimationFrame(scroll);
    });
  }, []);
  const closeOrder = useCallback(() => {
    setOrderState((s) => ({ ...s, open: false }));
  }, []);

  useEffect(() => () => forceUnlockPageScroll(), []);

  useEffect(() => {
    document.title = PAGE_TITLE_HOME;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", META_DESCRIPTION_HOME);
  }, []);

  useEffect(() => {
    if (!location.hash) return;
    const targetId = location.hash.slice(1);
    if (!targetId) return;

    let attempts = 0;
    const maxAttempts = 12;
    const scrollToHashTarget = () => {
      const target = document.getElementById(targetId);
      if (!target) {
        attempts += 1;
        if (attempts < maxAttempts) {
          window.setTimeout(scrollToHashTarget, 80);
        }
        return;
      }
      const nav = document.querySelector("[data-testid='site-nav']");
      const navHeight = nav?.getBoundingClientRect().height || 0;
      const sectionTop = target.getBoundingClientRect().top + window.scrollY;
      const targetTop = Math.max(0, sectionTop - navHeight - 16);
      window.scrollTo({ top: targetTop, behavior: "smooth" });
    };

    scrollToHashTarget();
  }, [location.hash]);

  useEffect(() => {
    if (process.env.NODE_ENV === "production" || typeof window === "undefined") {
      return undefined;
    }

    window.__modalDebug = () => ({
      orderOpen: orderState.open,
      trainersOpen,
      aerobicInfoOpen,
      scrollLock: getScrollLockState(),
      overlayCount: document.querySelectorAll("[data-overlay-root='true']").length,
    });

    return () => {
      delete window.__modalDebug;
    };
  }, [aerobicInfoOpen, orderState.open, trainersOpen]);

  return (
    <main className="app-bg min-h-screen text-[var(--text)]" data-testid="max-fitness-app">
      <Nav onOpenOrder={scrollToPricing} />
      <Hero onOpenOrder={scrollToPricing} onOpenTrainers={() => setTrainersOpen(true)} />
      <Bento />
      <Gallery />
      <Pricing
        onOpenOrder={openOrder}
        onOpenAerobicInfo={() => setAerobicInfoOpen(true)}
        onOpenTrainers={() => setTrainersOpen(true)}
      />
      <Faq />
      <Contact />
      <Footer />
      <OrderModal
        open={orderState.open}
        onClose={closeOrder}
        initialPlanId={orderState.planId}
      />
      <Suspense fallback={null}>
        <TrainersModal open={trainersOpen} onClose={() => setTrainersOpen(false)} />
        <AerobicInfoModal open={aerobicInfoOpen} onClose={() => setAerobicInfoOpen(false)} />
      </Suspense>
    </main>
  );
};

const MaxFitness = () => (
  <ThemeProvider>
    <I18nProvider>
      <MaxFitnessInner />
    </I18nProvider>
  </ThemeProvider>
);

export default MaxFitness;
